import { useCallback, useEffect, useState } from "react";
import { APP_CONFIG, MARKET_SYMBOLS } from "../config";

export type LoadState = "idle" | "loading" | "success" | "empty" | "error" | "offline" | "retrying";

export type TokenMarket = {
  priceUsd: number | null;
  change24h: number | null;
  volume24h: number | null;
  liquidityUsd: number | null;
  marketCap: number | null;
  fdv: number | null;
  buys24h: number | null;
  sells24h: number | null;
  trades24h: number | null;
  high24h: number | null;
  low24h: number | null;
  pairAddress: string;
  pairName: string;
  dex: string;
  chain: string;
  updatedAt: Date;
  sourceUrl: string;
};

export type TickerItem = {
  symbol: string;
  pair: string;
  price: number;
  change24h: number;
};

type Resource<T> = {
  state: LoadState;
  data: T | null;
  error: Error | null;
  reload: () => void;
};

type DexPair = {
  chainId?: string;
  dexId?: string;
  url?: string;
  pairAddress?: string;
  baseToken?: { address?: string; name?: string; symbol?: string };
  quoteToken?: { address?: string; name?: string; symbol?: string };
  priceUsd?: string;
  txns?: { h24?: { buys?: number; sells?: number } };
  volume?: { h24?: number };
  priceChange?: { h24?: number };
  liquidity?: { usd?: number };
  marketCap?: number;
  fdv?: number;
};

const apiProxy = (import.meta.env.VITE_API_PROXY as string | undefined)?.replace(/\/$/, "");
const withProxy = (url: string) => apiProxy ? `${apiProxy}?url=${encodeURIComponent(url)}` : url;
let dayRangeCache: { high: number | null; low: number | null; cachedAt: number } | null = null;

async function fetchJson<T>(url: string, retries = 1): Promise<T> {
  let lastError = new Error("Request failed");
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), APP_CONFIG.api.requestTimeoutMs);
    try {
      const response = await fetch(withProxy(url), {
        signal: controller.signal,
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json() as T;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Unknown request error");
      if (attempt < retries) await new Promise((resolve) => window.setTimeout(resolve, 650 * (attempt + 1)));
    } finally {
      window.clearTimeout(timeout);
    }
  }
  throw lastError;
}

async function fetchDayRange() {
  if (dayRangeCache && Date.now() - dayRangeCache.cachedAt < 300_000) {
    return { high: dayRangeCache.high, low: dayRangeCache.low };
  }
  const endpoint = `${APP_CONFIG.api.tokenOhlcv}${APP_CONFIG.token.pairAddress.toLowerCase()}/ohlcv/hour?aggregate=1&limit=24&currency=usd&token=base`;
  const payload = await fetchJson<{ data?: { attributes?: { ohlcv_list?: number[][] } } }>(endpoint, 1);
  const rows = payload.data?.attributes?.ohlcv_list;
  if (!Array.isArray(rows) || rows.length === 0) return { high: null, low: null };
  const highs = rows.map((row) => Number(row[2])).filter(Number.isFinite);
  const lows = rows.map((row) => Number(row[3])).filter(Number.isFinite);
  const range = {
    high: highs.length ? Math.max(...highs) : null,
    low: lows.length ? Math.min(...lows) : null,
  };
  dayRangeCache = { ...range, cachedAt: Date.now() };
  return range;
}

async function fetchTokenMarket(): Promise<TokenMarket | null> {
  const contract = APP_CONFIG.token.contract.toLowerCase();
  const endpoint = `${APP_CONFIG.api.tokenMarket}${contract}`;
  const [marketPayload, range] = await Promise.all([
    fetchJson<{ pairs?: DexPair[] }>(endpoint, 2),
    fetchDayRange().catch(() => ({ high: null, low: null })),
  ]);

  const verifiedPairs = (marketPayload.pairs ?? []).filter((pair) => {
    const base = pair.baseToken?.address?.toLowerCase();
    const quote = pair.quoteToken?.address?.toLowerCase();
    return pair.chainId === APP_CONFIG.token.chainId && (base === contract || quote === contract);
  });
  const selected = verifiedPairs.sort((a, b) => (b.liquidity?.usd ?? 0) - (a.liquidity?.usd ?? 0))[0];
  if (!selected?.pairAddress) return null;

  const buys = finiteOrNull(selected.txns?.h24?.buys);
  const sells = finiteOrNull(selected.txns?.h24?.sells);
  return {
    priceUsd: finiteOrNull(Number(selected.priceUsd)),
    change24h: finiteOrNull(selected.priceChange?.h24),
    volume24h: finiteOrNull(selected.volume?.h24),
    liquidityUsd: finiteOrNull(selected.liquidity?.usd),
    marketCap: finiteOrNull(selected.marketCap),
    fdv: finiteOrNull(selected.fdv),
    buys24h: buys,
    sells24h: sells,
    trades24h: buys !== null && sells !== null ? buys + sells : null,
    high24h: range.high,
    low24h: range.low,
    pairAddress: selected.pairAddress,
    pairName: `${selected.baseToken?.symbol ?? APP_CONFIG.token.symbol} / ${selected.quoteToken?.symbol ?? "WBNB"}`,
    dex: selected.dexId === "pancakeswap" ? "PancakeSwap V2" : selected.dexId ?? APP_CONFIG.token.dex,
    chain: APP_CONFIG.token.chain,
    updatedAt: new Date(),
    sourceUrl: selected.url ?? APP_CONFIG.token.dexScreener,
  };
}

async function fetchTickers(): Promise<TickerItem[]> {
  const ids = MARKET_SYMBOLS.map((item) => item.id).join(",");
  const endpoint = `${APP_CONFIG.api.mainstreamMarket}?ids=${encodeURIComponent(ids)}&vs_currencies=usd&include_24hr_change=true`;
  const payload = await fetchJson<Record<string, { usd?: number; usd_24h_change?: number }>>(endpoint, 1);
  return MARKET_SYMBOLS.flatMap((item) => {
    const row = payload[item.id];
    const price = Number(row?.usd);
    const change24h = Number(row?.usd_24h_change);
    if (!Number.isFinite(price) || !Number.isFinite(change24h)) return [];
    return [{ symbol: item.symbol, pair: item.pair, price, change24h }];
  });
}

function finiteOrNull(value: number | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function usePollingResource<T>(loader: () => Promise<T | null>, refreshMs: number): Resource<T> {
  const [state, setState] = useState<LoadState>("idle");
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [requestId, setRequestId] = useState(0);

  const reload = useCallback(() => {
    setState((current) => current === "error" || current === "offline" ? "retrying" : "loading");
    setRequestId((value) => value + 1);
  }, []);

  useEffect(() => {
    let active = true;
    let timer = 0;
    const run = async (background = false) => {
      if (!navigator.onLine) {
        if (active) setState("offline");
        return;
      }
      if (!background && active) setState((current) => current === "retrying" ? "retrying" : "loading");
      try {
        const next = await loader();
        if (!active) return;
        setData(next);
        setError(null);
        setState(next === null || (Array.isArray(next) && next.length === 0) ? "empty" : "success");
      } catch (caught) {
        if (!active) return;
        setError(caught instanceof Error ? caught : new Error("Unknown request error"));
        setState(navigator.onLine ? "error" : "offline");
      }
    };
    void run();
    timer = window.setInterval(() => void run(true), refreshMs);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [loader, refreshMs, requestId]);

  return { state, data, error, reload };
}

export function useTokenMarket(): Resource<TokenMarket> {
  return usePollingResource(fetchTokenMarket, APP_CONFIG.api.marketRefreshMs);
}

export function useMainstreamTickers(): Resource<TickerItem[]> {
  return usePollingResource(fetchTickers, APP_CONFIG.api.tickerRefreshMs);
}

export function formatUsd(value: number | null, compact = true): string {
  if (value === null || !Number.isFinite(value)) return "";
  if (Math.abs(value) > 0 && Math.abs(value) < 0.0001) {
    return `$${value.toLocaleString("en-US", { maximumSignificantDigits: 5, useGrouping: false })}`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact && Math.abs(value) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(value) < 1 ? 6 : 2,
  }).format(value);
}

export function formatInteger(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "";
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatTime(value: Date | null, lang: "zh" | "en"): string {
  if (!value) return "";
  return new Intl.DateTimeFormat(lang === "zh" ? "zh-CN" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kuala_Lumpur",
  }).format(value);
}
